import cn from 'classnames';
// Icons (fallback SVGs for icons not in MillionsIcon)
import VolumeMuted from '../../../../public/images/video-player/volume-muted.svg';
import Volume from '../../../../public/images/video-player/volume.svg';
import Fullscreen from '../../../../public/images/video-player/fullscreen.svg';
import Refresh from '../../../../public/images/video-player/refresh.svg';
import PlayNext from '../../../../public/images/video-player/play-next.svg';

import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import type { IconNames as MillionsIconNames } from 'ui3/MillionsIcon/MillionsIcon';

import styles from './VideoControlButton.module.scss';

const SVG_ICONS = {
  'volume-muted': VolumeMuted,
  volume: Volume,
  fullscreen: Fullscreen,
  refresh: Refresh,
  'play-next': PlayNext,
} as const;

const MILLIONS_ICON_MAP: Record<string, MillionsIconNames> = {
  pause: 'pauseSolid',
  play: 'play',
  settings: 'cog',
  camera: 'cameraOnOutline',
  'disabled-camera': 'cameraOffOutline',
  cog: 'cog',
  gear: 'cog',
  mic: 'microphoneOnOutline',
  'mic-muted': 'microphoneOffOutline',
  share: 'shareArrow',
};

const MILLIONS_ICON_KEYS = [
  'pause',
  'play',
  'settings',
  'camera',
  'disabled-camera',
  'cog',
  'gear',
  'mic',
  'mic-muted',
  'share',
] as const;

export type IconNames =
  | keyof typeof SVG_ICONS
  | typeof MILLIONS_ICON_KEYS[number];

type VideoControlButtonProps = {
  icon: IconNames;
} & React.ComponentPropsWithoutRef<'button'>;

const VideoControlButton = ({
  icon,
  className,
  ...rest
}: VideoControlButtonProps) => {
  const millionsName =
    MILLIONS_ICON_MAP[icon as keyof typeof MILLIONS_ICON_MAP];

  const SvgIcon = !millionsName
    ? SVG_ICONS[icon as keyof typeof SVG_ICONS]
    : null;

  const isMicrophoneIcon = icon === 'mic';

  return (
    <button
      className={cn(
        styles.root,
        { [styles.rootMicrophone]: isMicrophoneIcon },
        className
      )}
      {...rest}
    >
      {millionsName ? (
        <MillionsIcon
          name={millionsName}
          size={24}
          aria-hidden="true"
          role="presentation"
          focusable="false"
          data-icon-name={icon}
        />
      ) : SvgIcon ? (
        <SvgIcon
          aria-hidden="true"
          role="presentation"
          focusable="false"
          data-icon-name={icon}
        />
      ) : null}
    </button>
  );
};

export default VideoControlButton;
